import * as React from 'react';
import { useState } from 'react';
import { getUnits } from '@/api/units';
import { Box, Container, Typography, useTheme } from '@mui/material';
import { useQuery } from '@tanstack/react-query';
import { toast } from 'sonner';

import { useUser } from '@/hooks/v2/use-user';
import { UnitsTable } from '@/pages/v2/units/components/units-table';
import { CommunityPageHeader } from '@/components/common/community-page-header';
import { Building as BuildingIcon } from '@phosphor-icons/react';

export function UnitsPage(): React.JSX.Element {
  const { community, isLoading: isLoadingUser } = useUser();
  const theme = useTheme();
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage, setRowsPerPage] = useState(15);

  const { data, isLoading, refetch } = useQuery({
    queryKey: [`${community?.id}-units`, currentPage, rowsPerPage],
    queryFn: () => getUnits({
      page: currentPage,
      per_page: rowsPerPage,
    }),
  });

  const units = data?.units;

  const handleOnSuccess = React.useCallback(() => {
    toast.success('Unit created successfully');
    refetch();
  }, [refetch]);

  const handleOnDeleteSuccess = React.useCallback(() => {
    toast.success('Unit deleted successfully');
    refetch();
  }, [refetch]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleRowsPerPageChange = (newRowsPerPage: number) => {
    setRowsPerPage(newRowsPerPage);
    setCurrentPage(1); // Reset to first page when changing rows per page
  };

  return (
    <Box
        component="main"
        sx={{
          flexGrow: 1,
          py: 3,
          backgroundColor: theme.palette.background.default,
          minHeight: '100vh',
        }}
      >
        <Container maxWidth="xl">
          <CommunityPageHeader
            icon={<BuildingIcon size={36} />}
            subtitle="Manage units in the community"
            title="Units"
          />
          <UnitsTable
            loading={isLoading || isLoadingUser}
            onBulkDeleteSuccess={() => refetch()}
            onDeleteSuccess={handleOnDeleteSuccess}
            onPageChange={handlePageChange}
            onRowsPerPageChange={handleRowsPerPageChange}
            onSuccess={handleOnSuccess}
            pagination={data?.meta}
            rows={units || []}
          />
        </Container>
      </Box>
  );
}
